import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, MapPin, Calendar, FileText, UserPlus, Plus, Trash2,
  ClipboardList, StickyNote,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Prix unitaire par type de panneau (FCFA/jour) — utilisé pour l'estimation
const PRIX_UNITAIRES = {
  'Panneau déroulant': 3000,
  'Panneau LED':       5000,
  'Grand format':      4000,
  'Panneau poteau':    2500,
};

// Types (correspondent aux type_panneau en DB et au devis)
// NB : `max` est un fallback — le vrai maximum est le nb réel d'emplacements de ce type en DB.
const TYPE_SECTIONS = [
  { key: 'Panneau déroulant', label: 'Panneaux déroulants',    accent: 'text-blue-600',    accentBg: 'bg-blue-50',     max: 10 },
  { key: 'Panneau LED',       label: 'Panneaux LED',           accent: 'text-emerald-600', accentBg: 'bg-emerald-50',  max: 3  },
  { key: 'Grand format',      label: 'Panneaux grand format',  accent: 'text-violet-600',  accentBg: 'bg-violet-50',   max: 1  },
  { key: 'Panneau poteau',    label: 'Panneaux poteaux',       accent: 'text-orange-600',  accentBg: 'bg-orange-50',   max: 4  },
];

const emptyEntreprise = () => ({
  nom: '', adresse: '', code_postal: '', ville: '',
  telephone: '', email: '', site_web: '', siret: '',
});

// ─────────── Row d'un emplacement dans une section ───────────
// { emp_id: '', affiches: 1 }

export default function NouveauReservationForm({ onClose, onCreated }) {
  const [entreprise, setEntreprise] = useState(emptyEntreprise());
  const [emplacementsByType, setEmplacementsByType] = useState({});
  const [rowsByType, setRowsByType] = useState({
    'Panneau déroulant': [],
    'Panneau LED':       [],
    'Grand format':      [],
    'Panneau poteau':    [],
  });
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [remarques, setRemarques] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pdfHtml, setPdfHtml] = useState('');
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/emplacements`);
        const grouped = { 'Panneau déroulant': [], 'Panneau LED': [], 'Grand format': [], 'Panneau poteau': [] };
        (r.data || []).forEach(e => {
          const t = e.type_panneau;
          if (grouped[t]) grouped[t].push(e);
        });
        setEmplacementsByType(grouped);
      } catch {
        toast.error("Impossible de charger les emplacements");
      }
    };
    load();
  }, []);

  // Nb dispo par type = compte réel des emplacements en DB
  const availableFor = (type) => (emplacementsByType[type] || []).length;

  // Nombre total d'affiches
  const totalAffiches = useMemo(() => {
    let n = 0;
    Object.values(rowsByType).forEach(rows => rows.forEach(r => n += Number(r.affiches) || 0));
    return n;
  }, [rowsByType]);

  // Nb de jours
  const nbJours = useMemo(() => {
    if (!dateDebut || !dateFin) return 0;
    const d1 = new Date(dateDebut), d2 = new Date(dateFin);
    if (isNaN(d1) || isNaN(d2)) return 0;
    return Math.max(0, Math.round((d2 - d1) / 86400000) + 1);
  }, [dateDebut, dateFin]);

  const montantTotal = useMemo(() => {
    let total = 0;
    for (const [type, rows] of Object.entries(rowsByType)) {
      const pu = PRIX_UNITAIRES[type] || 3000;
      rows.forEach(r => { total += (Number(r.affiches) || 0) * pu * nbJours; });
    }
    return total;
  }, [rowsByType, nbJours]);

  const fmtFcfa = (n) => (Number(n) || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';

  // ───────── Mutations rowsByType ─────────
  const setCountForType = (type, count) => {
    const list = emplacementsByType[type] || [];
    const currentRows = rowsByType[type] || [];
    const max = Math.min(count, availableFor(type), list.length);
    const newRows = [];
    for (let i = 0; i < max; i++) {
      newRows.push(currentRows[i] || { emp_id: list[i]?.id || '', affiches: 1 });
    }
    setRowsByType({ ...rowsByType, [type]: newRows });
  };

  const addRow = (type) => {
    const rows = rowsByType[type] || [];
    if (rows.length >= availableFor(type)) {
      toast.error(`Maximum ${availableFor(type)} emplacement(s) atteint pour ce type`);
      return;
    }
    const list = emplacementsByType[type] || [];
    const usedIds = new Set(rows.map(r => r.emp_id));
    const nextEmp = list.find(e => !usedIds.has(e.id));
    setRowsByType({
      ...rowsByType,
      [type]: [...rows, { emp_id: nextEmp?.id || '', affiches: 1 }],
    });
  };

  const removeRow = (type, idx) => {
    const rows = rowsByType[type] || [];
    setRowsByType({ ...rowsByType, [type]: rows.filter((_, i) => i !== idx) });
  };

  const updateRow = (type, idx, patch) => {
    const rows = rowsByType[type] || [];
    setRowsByType({
      ...rowsByType,
      [type]: rows.map((r, i) => i === idx ? { ...r, ...patch } : r),
    });
  };

  // ───────── Validation & Submit ─────────
  const validate = () => {
    if (!entreprise.nom.trim()) { toast.error("Nom de l'entreprise obligatoire"); return false; }
    if (totalAffiches === 0)   { toast.error("Ajoutez au moins 1 affiche"); return false; }
    if (!dateDebut || !dateFin){ toast.error("Renseignez les dates"); return false; }
    if (nbJours <= 0)          { toast.error("Date de fin invalide"); return false; }
    return true;
  };

  const findOrCreateProspect = async () => {
    let prospectId = null;
    try {
      const r = await axios.get(`${API}/api/prospects`);
      const found = (r.data || []).find(p =>
        (p.entreprise || '').trim().toLowerCase() === entreprise.nom.trim().toLowerCase()
      );
      if (found) prospectId = found.id;
    } catch {}
    if (prospectId) return { id: prospectId, created: false };

    const payload = {
      entreprise: entreprise.nom.trim(),
      nom_contact: entreprise.nom.trim(),
      email: entreprise.email || '',
      telephone: entreprise.telephone || '',
      adresse: [entreprise.adresse, entreprise.code_postal, entreprise.ville].filter(Boolean).join(', '),
      site_web: entreprise.site_web || '',
      siret: entreprise.siret || '',
      source: 'Réservation directe',
      statut: 'Prospect',
    };
    const r2 = await axios.post(`${API}/api/prospects`, payload);
    return { id: r2.data?.id || r2.data?.prospect_id, created: true };
  };

  const buildLignes = () => {
    const lignes = [];
    for (const [type, rows] of Object.entries(rowsByType)) {
      if (!rows.length) continue;
      const list = emplacementsByType[type] || [];
      const emplacements = [];
      const affiches_par_emplacement = {};
      let totalNb = 0;
      rows.forEach(r => {
        const emp = list.find(e => e.id === r.emp_id);
        if (!emp || !(Number(r.affiches) > 0)) return;
        emplacements.push(emp.nom);
        affiches_par_emplacement[emp.nom] = Number(r.affiches);
        totalNb += Number(r.affiches);
      });
      if (emplacements.length === 0) continue;
      lignes.push({
        type_panneau: type,
        designation: `Location ${type}`,
        emplacement: emplacements.join(' + '),
        emplacements,
        affiches_par_emplacement,
        nombre_affiches: totalNb,
        prix_unitaire_jour: PRIX_UNITAIRES[type] || 3000,
        duree_jours: nbJours || 30,
        date_debut: dateDebut,
        date_fin: dateFin,
      });
    }
    return lignes;
  };

  const handleConvertProspect = async () => {
    if (!entreprise.nom.trim()) { toast.error("Nom de l'entreprise requis"); return; }
    setSubmitting(true);
    try {
      const { id, created } = await findOrCreateProspect();
      if (id) toast.success(created ? "Prospect ajouté ✓" : "Cette entreprise est déjà dans vos prospects");
      else toast.error("Échec de la création du prospect");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la conversion");
    } finally {
      setSubmitting(false);
    }
  };

  const handleObtenirReservation = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // 1) Trouver / créer prospect
      await findOrCreateProspect();

      // 2) Créer la réservation avec lignes[]
      const lignes = buildLignes();
      await axios.post(`${API}/api/emplacement-reservations`, {
        entreprise: entreprise.nom.trim(),
        responsable: entreprise.nom.trim(),
        contact: entreprise.telephone || entreprise.email || '',
        motif: remarques || `Réservation ${totalAffiches} affiche(s)`,
        date_debut: dateDebut,
        date_fin: dateFin,
        montant: String(montantTotal),
        notes: remarques,
        lignes,
      });
      toast.success('Réservation créée ✓');
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ══════ INFO ENTREPRISE ══════ */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4 text-blue-600" />
          Informations de l'entreprise
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Nom de l'entreprise *" value={entreprise.nom} onChange={(v) => setEntreprise({ ...entreprise, nom: v })} placeholder="Ma Société SARL" testid="ent-nom" />
          <FormField label="Téléphone" value={entreprise.telephone} onChange={(v) => setEntreprise({ ...entreprise, telephone: v })} placeholder="01 23 45 67 89" testid="ent-tel" />
          <FormField label="Adresse" value={entreprise.adresse} onChange={(v) => setEntreprise({ ...entreprise, adresse: v })} placeholder="123 Avenue des Médias" testid="ent-adr" />
          <FormField label="Email" value={entreprise.email} onChange={(v) => setEntreprise({ ...entreprise, email: v })} placeholder="contact@masociete.fr" testid="ent-email" />
          <FormField label="Code postal" value={entreprise.code_postal} onChange={(v) => setEntreprise({ ...entreprise, code_postal: v })} placeholder="75000" testid="ent-cp" />
          <FormField label="Site web" value={entreprise.site_web} onChange={(v) => setEntreprise({ ...entreprise, site_web: v })} placeholder="www.masociete.fr" testid="ent-web" />
          <FormField label="Ville" value={entreprise.ville} onChange={(v) => setEntreprise({ ...entreprise, ville: v })} placeholder="Paris" testid="ent-ville" />
          <FormField label="N° SIRET" value={entreprise.siret} onChange={(v) => setEntreprise({ ...entreprise, siret: v })} placeholder="123 456 789 00012" testid="ent-siret" />
        </div>
      </section>

      {/* ══════ CHOIX PANNEAUX / EMPLACEMENTS ══════ */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-blue-600" />
          Choix des panneaux et emplacements
        </h3>

        {TYPE_SECTIONS.map((section, secIdx) => {
          const rows = rowsByType[section.key] || [];
          const dispoTotal = availableFor(section.key);
          const list = emplacementsByType[section.key] || [];
          return (
            <div key={section.key} className={secIdx < TYPE_SECTIONS.length - 1 ? 'mb-4 pb-4 border-b border-slate-100' : ''}>
              {/* Titre + compteur */}
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <p className={`text-sm font-bold ${section.accent}`}>{section.label}</p>
                  <p className="text-xs text-slate-500">({dispoTotal} emplacement{dispoTotal > 1 ? 's' : ''} max)</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-600">Nombre d'emplacements à réserver</Label>
                  <Select value={String(rows.length)} onValueChange={(v) => setCountForType(section.key, Number(v))}>
                    <SelectTrigger className="h-8 w-20" data-testid={`res-count-${section.key.replace(/\s/g, '-')}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: dispoTotal + 1 }).map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-500">(sur {dispoTotal} disponible{dispoTotal > 1 ? 's' : ''})</span>
                </div>
              </div>

              {/* Rows */}
              {rows.length > 0 && (
                <div className="space-y-2 mb-2">
                  {rows.map((row, idx) => (
                    <div key={idx} className={`flex items-center gap-3 rounded-md ${section.accentBg} px-3 py-2`}>
                      <span className="w-28 text-sm font-medium text-slate-700 flex-shrink-0">Emplacement {idx + 1}</span>
                      <Select
                        value={row.emp_id}
                        onValueChange={(v) => updateRow(section.key, idx, { emp_id: v })}
                      >
                        <SelectTrigger className="h-8 flex-1 min-w-[180px] bg-white" data-testid={`res-emp-${section.key.replace(/\s/g, '-')}-${idx}`}>
                          <SelectValue placeholder="Sélectionner un emplacement" />
                        </SelectTrigger>
                        <SelectContent>
                          {list.map(e => (
                            <SelectItem
                              key={e.id}
                              value={e.id}
                              disabled={rows.some((r, i) => i !== idx && r.emp_id === e.id)}
                            >{e.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={String(row.affiches)}
                        onValueChange={(v) => updateRow(section.key, idx, { affiches: Number(v) })}
                      >
                        <SelectTrigger className="h-8 w-52 bg-white" data-testid={`res-aff-${section.key.replace(/\s/g, '-')}-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const selectedEmp = list.find(e => e.id === row.emp_id);
                            const isPoteau = section.key === 'Panneau poteau';
                            const maxAff = isPoteau ? (selectedEmp?.capacite_poteaux || 25) : 10;
                            const unitLabel = isPoteau ? 'poteaux' : 'affiches';
                            return Array.from({ length: maxAff + 1 }).map((_, i) => (
                              <SelectItem key={i} value={String(i)}>Nombre de {unitLabel} : {i}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => removeRow(section.key, idx)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                        data-testid={`res-del-${section.key.replace(/\s/g, '-')}-${idx}`}
                        aria-label="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Bouton ajouter */}
              {rows.length < dispoTotal && (
                <button
                  type="button"
                  onClick={() => addRow(section.key)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold ${section.accent} hover:underline`}
                  data-testid={`res-add-${section.key.replace(/\s/g, '-')}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Ajouter un emplacement
                </button>
              )}
            </div>
          );
        })}
      </section>

      {/* ══════ PÉRIODE ══════ */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-blue-600" />
          Période de réservation
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Date de début *</Label>
            <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="h-9" data-testid="date-debut" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Date de fin *</Label>
            <Input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className="h-9" data-testid="date-fin" />
          </div>
        </div>
        {totalAffiches > 0 && nbJours > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs bg-slate-50 rounded-md px-3 py-2">
            <span className="text-slate-500">
              {totalAffiches} affiche{totalAffiches > 1 ? 's' : ''} · {nbJours} jour{nbJours > 1 ? 's' : ''}
            </span>
            <span className="font-bold text-blue-700 text-sm">Estimation : {fmtFcfa(montantTotal)}</span>
          </div>
        )}
      </section>

      {/* ══════ REMARQUES ══════ */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <StickyNote className="h-4 w-4 text-blue-600" />
          Remarques <span className="text-xs font-normal text-slate-400">(optionnel)</span>
        </h3>
        <Textarea
          rows={3}
          value={remarques}
          onChange={(e) => setRemarques(e.target.value)}
          placeholder="Ajouter des informations complémentaires…"
          data-testid="remarques"
        />
      </section>

      {/* ══════ ACTIONS ══════ */}
      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-slate-200 flex gap-2">
        <Button
          onClick={handleObtenirReservation}
          disabled={submitting}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2 h-11"
          data-testid="btn-obtenir-reservation"
        >
          <ClipboardList className="h-4 w-4" />
          {submitting ? 'Traitement…' : 'Obtenir la réservation'}
        </Button>
        <Button
          onClick={handleConvertProspect}
          disabled={submitting}
          className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2 h-11"
          data-testid="btn-convert-prospect"
        >
          <UserPlus className="h-4 w-4" />
          {submitting ? 'Traitement…' : 'Convertir en prospect'}
        </Button>
      </div>

      {/* Modal PDF preview (optionnel) */}
      {showPdf && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPdf(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[92vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Aperçu</h3>
              <Button size="sm" variant="outline" onClick={() => setShowPdf(false)}>Fermer</Button>
            </div>
            <div className="border rounded p-3" dangerouslySetInnerHTML={{ __html: pdfHtml }} />
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9"
        data-testid={testid}
      />
    </div>
  );
}
